import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Smartphone,
  Wrench,
  Package,
  ShoppingCart,
  Settings,
  BarChart3,
  MessageSquare,
  LogOut,
  ChevronLeft,
  ChevronRight,
  UserCog,
  Cog,
  Plug,
  Calculator,
  FileText,
  Printer
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const { user, logout, hasPermission } = useAuth()
  const location = useLocation()

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      roles: ['ADMIN', 'TECNICO', 'VENDEDOR']
    },
    {
      name: 'Clientes',
      href: '/clientes',
      icon: Users,
      roles: ['ADMIN', 'TECNICO', 'VENDEDOR']
    },
    {
      name: 'Equipamentos',
      href: '/equipamentos',
      icon: Smartphone,
      roles: ['ADMIN', 'TECNICO', 'VENDEDOR']
    },
    {
      name: 'Ordens de Serviço',
      href: '/ordens-servico',
      icon: Wrench,
      roles: ['ADMIN', 'TECNICO']
    },
    {
      name: 'Orçamentos',
      href: '/orcamentos',
      icon: Calculator,
      roles: ['ADMIN', 'TECNICO', 'VENDEDOR']
    },
    {
      name: 'Cupons Térmicos',
      href: '/cupons-termicos',
      icon: Printer,
      roles: ['ADMIN']
    },
    {
      name: 'Stock',
      href: '/estoque',
      icon: Package,
      roles: ['ADMIN', 'TECNICO', 'VENDEDOR']
    },
    {
      name: 'Serviços',
      href: '/servicos',
      icon: Cog,
      roles: ['ADMIN', 'TECNICO']
    },
    {
      name: 'Vendas',
      href: '/vendas',
      icon: ShoppingCart,
      roles: ['ADMIN', 'VENDEDOR']
    },
    {
      name: 'Relatórios',
      href: '/relatorios',
      icon: BarChart3,
      roles: ['ADMIN']
    },
    {
      name: 'Notificações',
      href: '/notificacoes',
      icon: MessageSquare,
      roles: ['ADMIN', 'TECNICO']
    },
    {
      name: 'Integrações',
      href: '/integracoes',
      icon: Plug,
      roles: ['ADMIN'],
      subItems: [
        {
          name: 'WhatsApp',
          href: '/integracoes/whatsapp',
          roles: ['ADMIN']
        },
        {
          name: 'Email',
          href: '/integracoes/email',
          roles: ['ADMIN']
        }
      ]
    },
    {
      name: 'Utilizadores',
      href: '/usuarios',
      icon: UserCog,
      roles: ['ADMIN']
    },
    {
      name: 'Configurações',
      href: '/configuracoes',
      icon: Settings,
      roles: ['ADMIN'],
      subItems: [
        {
          name: 'Estados OS',
          href: '/configuracoes/estados-os',
          roles: ['ADMIN']
        }
      ]
    }
  ]

  const filteredNavigation = navigationItems.filter(item =>
    hasPermission(item.roles as any)
  )

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Erro ao fazer logout:', error)
    }
  }

  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 transition-all duration-300 ease-in-out flex flex-col
          ${isOpen ? 'w-64' : 'w-16'}
          lg:relative lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          {isOpen && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900">NexaMakers</span>
            </div>
          )}
          
          <button
            onClick={onToggle}
            className="p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {isOpen ? (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto min-h-0">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname.startsWith(item.href)
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => {
                  // Fechar sidebar automaticamente em mobile quando um item é clicado
                  if (window.innerWidth < 1024) { // lg breakpoint
                    onToggle()
                  }
                }}
                className={`
                  group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                  ${isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                title={!isOpen ? item.name : undefined}
              >
                <item.icon
                  className={`
                    flex-shrink-0 w-5 h-5
                    ${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}
                    ${isOpen ? 'mr-3' : 'mx-auto'}
                  `}
                />
                {isOpen && (
                  <span className="truncate">{item.name}</span>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="border-t border-gray-200 p-4 pb-20 lg:pb-4">
          {isOpen && user && (
            <div className="mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-700">
                    {user.nome.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.nome}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user.tipo.charAt(0) + user.tipo.slice(1).toLowerCase()}
                  </p>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleLogout}
            className={`
              group flex items-center w-full px-2 py-2 text-sm font-medium text-gray-600 rounded-md
              hover:bg-gray-50 hover:text-gray-900 transition-colors
            `}
            title={!isOpen ? 'Sair' : undefined}
          >
            <LogOut
              className={`
                flex-shrink-0 w-5 h-5 text-gray-400 group-hover:text-gray-500
                ${isOpen ? 'mr-3' : 'mx-auto'}
              `}
            />
            {isOpen && <span>Sair</span>}
          </button>
        </div>
      </div>
    </>
  )
}

export default Sidebar